import { getSupabase } from '@/lib/supabase';
import type { PlayerEvent } from '@/schemas';
import type { UserId } from '@/schemas/ids';
import type { RealtimeSubscription } from '../realtime';
import { decodeRowToPlayerEvent, type PlayerEventRow } from '../sync/codec';

export async function subscribeToPlayerEvents(
  userId: UserId,
  onEvent: (event: PlayerEvent) => void,
): Promise<RealtimeSubscription> {
  const userIdString = String(userId);
  const client = getSupabase();
  const {
    data: { session },
  } = await client.auth.getSession();
  if (!session?.user) {
    throw new Error('User must be authenticated to subscribe to player events');
  }

  const channel = client.channel(`player-events:${userIdString}`);

  const typedChannel = channel as unknown as {
    on: (
      type: 'postgres_changes',
      config: {
        event: 'INSERT' | 'UPDATE';
        schema: 'public';
        table: 'player_events';
        filter: string;
      },
      callback: (payload: { new: PlayerEventRow }) => void,
    ) => typeof channel;
  };
  const subscribe = (event: 'INSERT' | 'UPDATE') => {
    typedChannel.on(
      'postgres_changes',
      {
        event,
        schema: 'public',
        table: 'player_events',
        filter: `user_id=eq.${userIdString}`,
      },
      (payload: { new: PlayerEventRow }) => {
        try {
          const playerEvent = decodeRowToPlayerEvent(payload.new);
          onEvent(playerEvent);
        } catch (error: unknown) {
          const err = error instanceof Error ? error : new Error(String(error));
          console.warn('Failed to decode realtime player event', err);
        }
      },
    );
  };

  subscribe('INSERT');
  subscribe('UPDATE');

  channel.subscribe();

  return {
    unsubscribe: () => {
      void client.removeChannel(channel);
    },
  };
}
